import { useQuery } from "@apollo/client";
import DisplayFormatSelector, { DisplayFormat } from "components/charts/modern/inputs/DisplayFormatSelector";
import TickerSymbolSelector from "components/charts/modern/inputs/TickerSymbolSelector";
import { SELECTED_ISSUERS_ATOM } from "components/charts/modern/overlay/IssuersSelect/atoms";
import LoadingSkeleton from "components/pages/TemplatesGalleryPage/LoadingSkeleton";
import classNames from "components/ui/classNames";
import differenceInYears from "date-fns/differenceInYears";
import parseISO from "date-fns/parseISO";
import { useAtomValue } from "jotai";
import TrailingReturnsByTickerSymbolsQuery from "lib/graphql/queries/TrailingReturnsByTickerSymbolsQuery";
import type {
  ITrailingReturnsByTickerSymbolsQuery,
  ITrailingReturnsByTickerSymbolsQueryVariables,
} from "lib/graphql/queries/__generated__/TrailingReturnsByTickerSymbolsQuery.generated";
import useSelfie from "lib/hooks/useSelfie";
import { LockIcon } from "lucide-react";
import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

interface TrailingReturnsChartProps {
  tickerSymbols: string[];
}

export default function TrailingReturnsChart({ tickerSymbols }: TrailingReturnsChartProps) {
  const [displayFormat, setDisplayFormat] = useState(DisplayFormat.Percent);
  const [selectedTickerSymbol, setSelectedTickerSymbol] = useState(tickerSymbols[0]);

  const intl = useIntl();
  const selfie = useSelfie();
  const selectedIssuers = useAtomValue(SELECTED_ISSUERS_ATOM);
  const tickerSymbolsForSeries = [selectedTickerSymbol].concat(selectedIssuers.map((node) => node.tickerSymbol));

  const { data, loading, previousData } = useQuery<
    ITrailingReturnsByTickerSymbolsQuery,
    ITrailingReturnsByTickerSymbolsQueryVariables
  >(TrailingReturnsByTickerSymbolsQuery, {
    variables: {
      tickerSymbols: [...new Set(tickerSymbolsForSeries)].slice(0, 5),
    },
  });
  const trailingReturns =
    (loading && !data ? previousData?.trailingReturnsByTickerSymbols : data?.trailingReturnsByTickerSymbols) || [];

  const percentageFormatter = new Intl.NumberFormat("en-US", {
    style: "percent",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

  const percentageFormatterWithFractionDigits = new Intl.NumberFormat("en-US", {
    style: "percent",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

  const valueFormatter = new Intl.NumberFormat(intl.locale, {
    notation: "compact",
    compactDisplay: "short",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

  const returnRateFormatter = (returnRate: number, priceClose: number, formatAsPercentage: boolean) => {
    const isBigValue = returnRate >= 10000;
    const returnValue = returnRate * 100;
    const enabledFractionDigits = returnValue < 10 && returnValue > -10;

    if (formatAsPercentage) {
      if (isBigValue) {
        return `${valueFormatter.format(returnRate)} %`;
      }

      if (enabledFractionDigits) {
        return percentageFormatterWithFractionDigits.format(returnRate);
      }

      return percentageFormatter.format(returnRate);
    }

    if (isBigValue) {
      return `${percentageFormatter.format(returnRate)} %`;
    }

    return valueFormatter.format(priceClose);
  };

  if (loading) {
    return <LoadingSkeleton count={1} className="min-h-96 xl:h-full" />;
  }

  return (
    <div className="h-full w-full space-y-8">
      <div className="relative flex w-full flex-col items-center justify-between sm:flex-row">
        <div className="flex w-full flex-col items-center space-y-4 md:flex-row md:space-x-4 md:space-y-0">
          {tickerSymbolsForSeries.length >= 2 && (
            <TickerSymbolSelector
              type="single"
              tickerSymbols={tickerSymbols}
              value={[selectedTickerSymbol]}
              onChange={(tickerSymbols) => setSelectedTickerSymbol(tickerSymbols[0])}
            />
          )}

          <DisplayFormatSelector value={displayFormat} onChange={setDisplayFormat} />
        </div>
      </div>

      <div className="grid grid-cols-2 items-center gap-4 md:w-fit md:grid-cols-4 md:gap-4 lg:grid-cols-5 xl:grid-cols-4">
        {trailingReturns.map((node) => {
          if (!node.trailingReturns) {
            return null;
          }

          const formatDifferenceInYears = intl.formatMessage(
            { defaultMessage: "{years} anos", id: "kUEOEk" },
            {
              years: differenceInYears(parseISO(node.trailingReturns.maxDate), parseISO(node.trailingReturns.minDate)),
            },
          );

          return [
            {
              key: "oneDay",
              returnRate: node.trailingReturns.oneDay,
              price: node.trailingReturns.prices?.oneDay || null,
              label: intl.formatMessage({ defaultMessage: "1 dia", id: "t3JbPb" }),
              mustBePremium: false,
            },
            {
              key: "oneWeek",
              returnRate: node.trailingReturns.oneWeek,
              price: node.trailingReturns.prices?.oneWeek || null,
              label: intl.formatMessage({ defaultMessage: "1 semana", id: "Z7mLAK" }),
              mustBePremium: false,
            },
            {
              key: "oneMonth",
              returnRate: node.trailingReturns.oneMonth,
              price: node.trailingReturns.prices?.oneMonth || null,
              label: intl.formatMessage({ defaultMessage: "1 mês", id: "T0sgAr" }),
              mustBePremium: false,
            },
            {
              key: "oneYear",
              returnRate: node.trailingReturns.oneYear,
              price: node.trailingReturns.prices?.oneYear || null,
              label: intl.formatMessage({ defaultMessage: "1 ano", id: "Lc4l9T" }),
              mustBePremium: false,
            },
            {
              key: "fiveYears",
              returnRate: node.trailingReturns.fiveYears,
              price: node.trailingReturns.prices?.fiveYears || null,
              label: intl.formatMessage({ defaultMessage: "5 anos", id: "ZfBB6o" }),
              mustBePremium: false,
              className: "lg:row-start-2",
            },
            {
              key: "fiveYearsAnnualized",
              returnRate: node.trailingReturns.fiveYearsAnnualized,
              price: node.trailingReturns.prices?.fiveYears || null,
              label: intl.formatMessage({ defaultMessage: "5 anos", id: "ZfBB6o" }),
              mustBePremium: false,
              isAnnualized: true,
              className: "lg:row-start-3",
            },
            {
              key: "tenYears",
              returnRate: node.trailingReturns.tenYears,
              price: node.trailingReturns.prices?.tenYears || null,
              label: intl.formatMessage({ defaultMessage: "10 anos", id: "SQE0oT" }),
              mustBePremium: true,
              trialHasAccess: true,
              className: "lg:row-start-2",
            },
            {
              key: "tenYearsAnnualized",
              returnRate: node.trailingReturns.tenYearsAnnualized,
              price: node.trailingReturns.prices?.tenYears || null,
              label: intl.formatMessage({ defaultMessage: "10 anos", id: "SQE0oT" }),
              mustBePremium: true,
              trialHasAccess: true,
              isAnnualized: true,
              className: "lg:row-start-3",
            },
            {
              key: "allTime",
              returnRate: node.trailingReturns.allTime,
              price: node.trailingReturns.prices?.allTime || null,
              label: formatDifferenceInYears,
              mustBePremium: true,
              className: "lg:row-start-2",
            },
            {
              key: "allTimeAnnualized",
              returnRate: node.trailingReturns.allTimeAnnualized,
              price: node.trailingReturns.prices?.allTime || null,
              label: formatDifferenceInYears,
              mustBePremium: true,
              isAnnualized: true,
              className: "lg:row-start-3",
            },
          ].map((node) => {
            const isPremium = selfie?.isPremium;
            if (
              (node.mustBePremium && !isPremium) ||
              (node.mustBePremium && !node.trialHasAccess && isPremium && selfie?.isTrialing)
            ) {
              return (
                <div key={node.key} className={classNames("h-full rounded-xl bg-slate-50 p-4", node.className)}>
                  <div className={classNames("h-8 font-bold text-slate-600")}>
                    <LockIcon />
                  </div>
                  <h3 className="whitespace-nowrap text-sm font-bold tracking-wide text-slate-700">{node.label}</h3>
                  {!node.isAnnualized && node.price?.tradeDate && (
                    <h6 className="mt-1 text-xs font-medium tracking-normal text-slate-500">
                      {intl.formatDate(parseISO(node.price?.tradeDate), {
                        year: "numeric",
                        month: "numeric",
                        day: "numeric",
                      })}
                    </h6>
                  )}
                  {node.isAnnualized && (
                    <h6 className="mt-1 text-xs font-medium tracking-normal text-slate-500">
                      <FormattedMessage defaultMessage="Anualizado" id="6mGeOG" />
                    </h6>
                  )}
                </div>
              );
            }
            if (node.returnRate === null) {
              return null;
            }
            const { returnRate } = node;
            const isNegativeReturn = returnRate < 0;
            const isPositiveReturn = returnRate > 0;
            const isNeutralReturn = returnRate >= 0 && returnRate < 0.1;

            return (
              <div key={node.key} className={classNames("rounded-xl bg-slate-50 p-4", node.className)}>
                <h1
                  className={classNames("text-2xl font-bold text-slate-600", {
                    "text-rose-600": isNegativeReturn,
                    "text-emerald-600": isPositiveReturn,
                    "text-slate-600": isNeutralReturn,
                  })}
                >
                  {returnRateFormatter(returnRate, node.price?.close || 0, displayFormat === DisplayFormat.Percent)}
                </h1>

                <h3 className="whitespace-nowrap text-sm font-bold tracking-wide text-slate-700">{node.label}</h3>
                {!node.isAnnualized && node.price?.tradeDate && (
                  <h6 className="mt-1 text-xs font-medium tracking-normal text-slate-500">
                    {intl.formatDate(parseISO(node.price?.tradeDate), {
                      year: "numeric",
                      month: "numeric",
                      day: "numeric",
                    })}
                  </h6>
                )}
                {node.isAnnualized && (
                  <h6 className="mt-1 text-xs font-medium tracking-normal text-slate-500">
                    <FormattedMessage defaultMessage="Anualizado" id="6mGeOG" />
                  </h6>
                )}
              </div>
            );
          });
        })}
      </div>
    </div>
  );
}
